import React, { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import {
  Box, Typography, Button, Stack, Paper, Divider, Chip,
  TextField, useTheme, Alert, ToggleButtonGroup, ToggleButton,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import FenceIcon from '@mui/icons-material/Fence';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import 'leaflet-draw/dist/leaflet.draw.css';
import 'leaflet-fullscreen/dist/leaflet.fullscreen.css';
import 'leaflet-fullscreen';
import { readFileAsText, parseAnyGeoFile, haversineDistance } from '../../../utils/geoUtils';
import SEO, { createSoftwareAppSchema } from '../../../components/SEO';

import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
let DefaultIcon = L.icon({ iconUrl: icon, shadowUrl: iconShadow, iconAnchor: [12, 41] });
L.Marker.prototype.options.icon = DefaultIcon;

function pointInPolygon(point, polygon) {
  const [x, y] = point;
  let inside = false;
  for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
    const [xi, yi] = polygon[i];
    const [xj, yj] = polygon[j];
    if ((yi > y) !== (yj > y) && x < ((xj - xi) * (y - yi)) / (yj - yi) + xi) {
      inside = !inside;
    }
  }
  return inside;
}

function pointInCircle(point, center, radiusM) {
  return haversineDistance(point, center) <= radiusM;
}

function GeofenceVisualizer() {
  const theme = useTheme();
  const mapRef = useRef(null);
  const drawnRef = useRef(null);
  const pointsLayerRef = useRef(null);
  const [geofences, setGeofences] = useState([]);
  const [dataPoints, setDataPoints] = useState([]);
  const [error, setError] = useState('');

  // Initialize map
  useEffect(() => {
    if (mapRef.current) return;
    const map = L.map('geofence-map', {
      fullscreenControl: true,
    }).setView([39.8283, -98.5795], 4);

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '&copy; OSM',
    }).addTo(map);

    const darkLayer = L.tileLayer('https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png', { attribution: '&copy; CartoDB' });
    const satLayer = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', { attribution: '&copy; Esri' });
    L.control.layers({ 'Streets': L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png').addTo(map), 'Dark': darkLayer, 'Satellite': satLayer }, null, { position: 'topright' }).addTo(map);

    const drawnItems = new L.FeatureGroup();
    map.addLayer(drawnItems);

    const drawControl = new L.Control.Draw({
      position: 'topleft',
      edit: { featureGroup: drawnItems },
      draw: {
        polygon: { shapeOptions: { color: '#6366f1', fillOpacity: 0.15 } },
        rectangle: { shapeOptions: { color: '#6366f1', fillOpacity: 0.15 } },
        circle: { shapeOptions: { color: '#6366f1', fillOpacity: 0.15 } },
        polyline: false, marker: false, circlemarker: false,
      },
    });
    map.addControl(drawControl);

    map.on(L.Draw.Event.CREATED, (event) => {
      const layer = event.layer;
      drawnItems.addLayer(layer);
      updateGeofences(drawnItems);
    });
    map.on('draw:edited', () => updateGeofences(drawnItems));
    map.on('draw:deleted', () => updateGeofences(drawnItems));

    const updateGeofences = (items) => {
      const fences = [];
      items.eachLayer((layer) => {
        if (layer instanceof L.Circle) {
          const center = layer.getLatLng();
          fences.push({ type: 'circle', center: [center.lng, center.lat], radius: layer.getRadius(), id: L.Util.stamp(layer) });
        } else if (layer.toGeoJSON) {
          const gj = layer.toGeoJSON();
          if (gj.geometry?.type === 'Polygon') {
            fences.push({ type: 'polygon', coordinates: gj.geometry.coordinates[0], id: L.Util.stamp(layer) });
          }
        }
      });
      setGeofences(fences);
    };

    L.control.scale({ position: 'bottomleft', imperial: false }).addTo(map);
    mapRef.current = map;
    drawnRef.current = drawnItems;
    pointsLayerRef.current = L.layerGroup().addTo(map);

    return () => { map.remove(); mapRef.current = null; };
  }, []);

  const handleUploadPoints = useCallback(async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    try {
      const text = await readFileAsText(file);
      const result = parseAnyGeoFile(text);
      if (result.error || !result.geoJson) throw new Error(result.error || 'Could not parse');

      const fc = result.geoJson;
      const pts = [];
      for (const f of fc.features) {
        if (f.geometry?.type === 'Point') {
          pts.push({ coord: f.geometry.coordinates, props: f.properties || {} });
        } else if (f.geometry?.type === 'LineString') {
          f.geometry.coordinates.forEach(c => pts.push({ coord: c, props: {} }));
        }
      }
      if (pts.length === 0) throw new Error('No point data found');
      setDataPoints(pts);
      setError('');
    } catch (err) {
      setError(err.message);
    }
  }, []);

  // Containment analysis
  const analysis = useMemo(() => {
    if (geofences.length === 0 || dataPoints.length === 0) return null;
    const results = dataPoints.map(pt => {
      const inside = [];
      geofences.forEach((fence, i) => {
        if (fence.type === 'polygon' && pointInPolygon(pt.coord, fence.coordinates)) {
          inside.push(i);
        } else if (fence.type === 'circle' && pointInCircle(pt.coord, fence.center, fence.radius)) {
          inside.push(i);
        }
      });
      return { ...pt, insideFences: inside, isInside: inside.length > 0 };
    });
    const insideCount = results.filter(r => r.isInside).length;
    return { results, insideCount, outsideCount: results.length - insideCount };
  }, [geofences, dataPoints]);

  // Update point markers on map
  useEffect(() => {
    const layer = pointsLayerRef.current;
    if (!layer) return;
    layer.clearLayers();
    if (!analysis) {
      dataPoints.forEach(pt => {
        L.circleMarker([pt.coord[1], pt.coord[0]], {
          radius: 4, fillColor: '#6366f1', color: '#fff', weight: 1, fillOpacity: 0.8,
        }).addTo(layer);
      });
      return;
    }
    analysis.results.forEach(r => {
      L.circleMarker([r.coord[1], r.coord[0]], {
        radius: 5,
        fillColor: r.isInside ? '#10b981' : '#ef4444',
        color: '#fff', weight: 1.5, fillOpacity: 0.85,
      }).bindTooltip(r.isInside ? `Inside fence(s): ${r.insideFences.map(i => i + 1).join(', ')}` : 'Outside all fences')
        .addTo(layer);
    });

    if (dataPoints.length > 0 && mapRef.current) {
      const bounds = L.latLngBounds(dataPoints.map(p => [p.coord[1], p.coord[0]]));
      if (bounds.isValid()) mapRef.current.fitBounds(bounds, { padding: [40, 40] });
    }
  }, [analysis, dataPoints]);

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      <SEO
        title="Geofence Visualizer & Containment Analysis | UmbrellaCo"
        description="Draw geofences and upload coordinate data to instantly analyze point-in-polygon containment. Free online spatial visualization tool."
        keywords="geofence visualizer, point in polygon, containment analysis, map geofencing online, spatial intersection"
        url="/spatial-tools/geofences"
        jsonLd={createSoftwareAppSchema(
          'Geofence Visualizer',
          'Draw geofences and analyze point containment instantly.',
          'https://umbrellaco.in/spatial-tools/geofences',
          ['Geofencing', 'Containment Analysis', 'Interactive Mapping']
        )}
      />
      <Box sx={{ px: 2, pt: 1, pb: 1, flexShrink: 0 }}>
        <Typography component="h1" variant="h6" fontWeight={700}>Geofence Visualizer & Containment</Typography>
        <Typography variant="body2" color="text.secondary">
          Draw virtual boundaries (polygons, circles) and upload data points to determine which coordinates fall inside or outside your geofences.
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexGrow: 1, height: 0 }}>
        {/* Sidebar */}
        <Box sx={{ width: 280, flexShrink: 0, overflow: 'auto', bgcolor: 'background.paper', borderRight: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1.5 }}>
              <FenceIcon fontSize="small" sx={{ color: 'primary.main' }} />
              <Typography variant="subtitle2" fontWeight={700}>Geofences</Typography>
            </Stack>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
              Use draw tools on the map to create polygon/rectangle/circle geofences
            </Typography>
            {geofences.length > 0 && (
              <Stack spacing={0.5}>
                {geofences.map((f, i) => (
                  <Chip key={f.id} size="small" label={`Fence ${i + 1}: ${f.type}`}
                    sx={{ fontWeight: 600, bgcolor: '#6366f122', color: '#6366f1' }} />
                ))}
              </Stack>
            )}
          </Box>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 1 }}>
              <AddLocationIcon fontSize="small" sx={{ color: '#f59e0b' }} />
              <Typography variant="subtitle2" fontWeight={700}>Data Points</Typography>
            </Stack>
            <Button component="label" variant="outlined" fullWidth size="small" startIcon={<UploadFileIcon />}>
              {dataPoints.length > 0 ? `${dataPoints.length} points loaded` : 'Upload Points'}
              <input type="file" hidden accept=".geojson,.json,.csv,.tsv,.txt" onChange={handleUploadPoints} />
            </Button>
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
          </Box>
          <Divider />
          {analysis && (
            <Box sx={{ p: 2 }}>
              <Typography variant="subtitle2" fontWeight={700} sx={{ mb: 1 }}>Containment Analysis</Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Inside</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#10b981' }}>{analysis.insideCount}</Typography>
                </Paper>
                <Paper variant="outlined" sx={{ p: 1, textAlign: 'center' }}>
                  <Typography variant="caption" color="text.secondary">Outside</Typography>
                  <Typography variant="body2" fontWeight={700} sx={{ color: '#ef4444' }}>{analysis.outsideCount}</Typography>
                </Paper>
              </Box>
              <Paper variant="outlined" sx={{ p: 1, mt: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">Containment Rate</Typography>
                <Typography variant="body1" fontWeight={700}>
                  {((analysis.insideCount / dataPoints.length) * 100).toFixed(1)}%
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
        {/* Map */}
        <Box sx={{ flexGrow: 1, height: '100%' }}>
          <div id="geofence-map" style={{ height: '100%', width: '100%' }} />
        </Box>
      </Box>
    </Box>
  );
}

export default GeofenceVisualizer;
